// Product copy shared across screens, ported from the approved mockup data
// (mockups/fable/assets/data.js). User-visible: the repo style rules bind
// every string here, and the reader-facing noun is "practice".
import type { SnoozeDuration } from "../shared/ontology.js";

// Deliberately generous: "later" is a legitimate answer to a health
// suggestion, and a product that only offers yes/no forces a no.
export const SNOOZE_OPTIONS: Array<{ id: SnoozeDuration; label: string; note?: string }> = [
  { id: "week", label: "In a week" },
  { id: "month", label: "In a month" },
  { id: "quarter", label: "In three months" },
  { id: "year", label: "Later this year" },
  {
    id: "season",
    label: "When the season changes",
    note: "Some practices genuinely suit a time of year. Cold showers in January are a different proposition to cold showers in July.",
  },
];

// Why someone is deferring: the most useful signal the product can
// collect, and it costs the user one tap.
export const SNOOZE_REASONS: string[] = [
  "Too much going on right now",
  "Too hard to fit into my life",
  "Costs more than I want to spend",
  "Wrong time of year",
  "My practice is already full",
  "Not convinced yet",
];

export interface DeclineOption {
  key: "already" | "notfit" | "expert" | "topic" | "risky";
  label: string;
  scope: "card" | "expert" | "topic";
  effect: string;
}

export const DECLINE_REASONS: DeclineOption[] = [
  {
    key: "already",
    label: "I already do this",
    scope: "card",
    effect: "Good. It moves to Passed and won’t count against you.",
  },
  { key: "notfit", label: "Not for me", scope: "card", effect: "It moves to Passed and stays out of your way." },
  {
    key: "expert",
    label: "Not interested in this expert",
    scope: "expert",
    effect: "You’ll stop seeing practices from them. You stay following them for the feed unless you unfollow.",
  },
  {
    key: "topic",
    label: "Not interested in this topic",
    scope: "topic",
    effect: "Switches the whole topic off, from everyone. You can turn it back on in settings.",
  },
  {
    key: "risky",
    label: "This seems unsafe to me",
    scope: "card",
    effect: "Goes to a reviewer as well as hiding the practice. Tell us more if you want, it’s optional.",
  },
];

// The 8 filterable domains for My Practice, in ontology order.
export const DOMAIN_FILTERS: Array<{ id: string; short: string; label: string }> = [
  { id: "nutrition", short: "Nutrition", label: "Nutrition and food" },
  { id: "movement", short: "Movement", label: "Movement and performance" },
  { id: "sleep", short: "Sleep", label: "Sleep and circadian health" },
  { id: "mind", short: "Mind", label: "Mind, behavior, and relationships" },
  { id: "environment", short: "Exposures", label: "Environment and exposures" },
  { id: "hormones", short: "Hormones", label: "Hormones, reproductive, and sexual health" },
  { id: "selfadmin", short: "Supplements", label: "Supplements, self-administered therapies, and health technology" },
  { id: "prevention", short: "Prevention", label: "Prevention and clinical care" },
];

// The accuracy and disclosure commitments. The first entry's second line
// was revised for decision 15 (automated verification replaced the named
// human reviewer); the rest is the approved mockup copy verbatim.
export const COMMITMENTS: Array<[string, string]> = [
  [
    "We won’t attribute something an expert didn’t say",
    "Every practice ties to an exact source span. If our agent inferred a number the source never stated, a dose, a duration, a threshold, that practice doesn’t ship. This is the most common extraction failure and the main thing the verification pass exists to catch.",
  ],
  [
    "We won’t hide a commercial relationship",
    "When an expert sells the thing they’re recommending, or co-founded the company behind it, that’s on the practice. Sinclair’s NMN practice names his company and his loudest critic’s employer, because you’re reading a fight between two interested parties.",
  ],
  [
    "We won’t present mechanism as outcome",
    "Mouse and yeast findings get labelled as such. This is the specific criticism named scientists make of several roster members, and the evidence labels are where we take it seriously.",
  ],
  [
    "We won’t let a settings choice be silent",
    "Whenever a topic setting hides something that would otherwise have reached you, the screen names the setting and links to the switch. A filter you can’t see is indistinguishable from a thin catalogue.",
  ],
  [
    "We won’t make a checklist carry what a checklist can’t",
    "Some content is an argument, not an action, disease-reversal claims, diagnostic reasoning, prescription decisions. It publishes as reading with full labelling. That’s a format judgement about checkboxes, not a judgement about whether you should hear it.",
  ],
  [
    "We won’t rank by who pays us",
    "No pay-to-rank at any tier, and affiliate economics are never an input to ordering. A Partner and an unclaimed profile get the same review standard.",
  ],
  [
    "We won’t rewrite your practice underneath you",
    "When a practice changes or a study is corrected, you keep the version you accepted and get told what moved. Practice history is append-only.",
  ],
];

export const CHIP_CLASS: Record<string, string> = {
  Established: "established",
  Emerging: "emerging",
  "Expert viewpoint": "viewpoint",
  Contested: "contested",
};
